import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Popover, Select, Tag as AntTag, Empty } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { TClientTags, TagWorkerType, TagsMap } from "@/ts/common-types";
import { TAGS_MODULE_MAPPING } from "@/ts/constants";
import { getActiveModule, getTagType } from "@/ts/utils";
import { FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import { showToast } from "@ts/modals";
import { createWorkers } from "@ts/utils";
import { FixedSizeList as List } from "react-window";

const LIST_HEIGHT = 256;
const ITEM_HEIGHT = 40;

type CustomTagProps = {
    inDropdown?: boolean;
    closeIcon?: React.ReactNode;
    value?: string;
    onClose?: () => void;
} & React.ComponentProps<typeof AntTag>;

const CustomTag: React.FC<CustomTagProps> = React.memo(
    ({ inDropdown, closeIcon, value, onClose, children, ...props }) => {
        const handleTagClose = useCallback(
            (e: React.MouseEvent) => {
                e.preventDefault();
                if (onClose) onClose();
            },
            [onClose]
        );

        return (
            <AntTag {...props} closable={!inDropdown} onClose={handleTagClose}>
                {children}
                {!inDropdown && closeIcon}
            </AntTag>
        );
    }
);

type RowProps = {
    index: number;
    style: React.CSSProperties;
    data: {
        tags: string[];
        recordType: string;
        onOptionClick: (tag: string) => void;
        selectedTags: string[]; // <-- This was missing
    };
};

const Row: React.FC<RowProps> = React.memo(({ index, style, data }) => {
    const { tags, recordType, onOptionClick, selectedTags } = data;
    const tag = tags[index];
    const tagName = tag.split("##")[1];
    const { tagType, tagFillColor, tagBorderColor } = getTagType(
        tag,
        recordType
    );

    const isSelected = selectedTags.includes(tag);
    const rowClass = `ant-select-item ant-select-item-option ${
        isSelected ? "ant-select-item-option-selected" : ""
    }`;

    return (
        <div
            style={style}
            className={rowClass}
            aria-selected={isSelected}
            onClick={() => onOptionClick(tag)}
        >
            <div className="ant-select-item-option-content">
                <CustomTag
                    key={tag}
                    role="button"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: "4px",
                        border: `1px solid ${tagBorderColor}`,
                        overflow: "hidden",
                        width: "160px",
                        paddingRight: 5,
                        paddingLeft: 0,
                    }}
                    inDropdown={true}
                >
                    <span
                        style={{
                            width: 25,
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: tagFillColor,
                            color: "white",
                            padding: "0 8px",
                            display: "flex",
                            borderRight: `1px solid ${tagBorderColor}`,
                            flexShrink: 0,
                        }}
                    >
                        {tagType[0]}
                    </span>
                    <span
                        style={{
                            padding: "0 8px",
                            color: "black",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            flexGrow: 1,
                        }}
                    >
                        {tagName}
                    </span>
                </CustomTag>
            </div>
            {isSelected && (
                <span
                    className="ant-select-item-option-state"
                    style={{ userSelect: "none" }}
                >
                    <CheckOutlined />
                </span>
            )}
        </div>
    );
});

type IBulkModalTagDropDownProps = {
    setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
    selectedTags: string[];
};

const BulkModalTagDropDown: React.FC<IBulkModalTagDropDownProps> = ({
    setSelectedTags,
    selectedTags,
}) => {
    const { activeModule } = getActiveModule();
    const { recordType, validRecordTypes } =
        TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const [avlTagsData, setAvlTagsData] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (!tagTypeList || tagTypeList.length === 0) return;
        setIsProcessing(true);
        createWorkers("tag", {
            type: TagWorkerType.PROCESS_TAGS,
            payload: {
                tagTypeList,
                recordType,
                validRecordTypes,
            },
        })
            .then((result: any) => setAvlTagsData(result?.payload || []))
            .catch((error) =>
                console.error("Error processing tag data:", error)
            )
            .finally(() => setIsProcessing(false));
    }, [tagTypeList, validRecordTypes, recordType]);

    const filteredTags = useMemo(() => {
        if (!searchText) return avlTagsData;
        return avlTagsData.filter((tag) =>
            tag.split("##")[1].toLowerCase().includes(searchText.toLowerCase())
        );
    }, [avlTagsData, searchText]);

    const handleSelectChange = useCallback(
        (value: string[]) => {
            const tagTypes: string[] = [];
            for (const tag of value) {
                const { tagType } = getTagType(tag, recordType);
                if (tagType && tagTypes.includes(tagType)) {
                    showToast(
                        "danger",
                        "You cannot select more than one tag of the same type."
                    );
                    return;
                }
                tagTypes.push(tagType);
            }
            setSelectedTags(value);
        },
        [recordType, setSelectedTags]
    );

    const handleOptionClick = useCallback(
        (clickedTag: string) => {
            const newSelection = selectedTags.includes(clickedTag)
                ? selectedTags.filter((t) => t !== clickedTag)
                : [...selectedTags, clickedTag];
            handleSelectChange(newSelection);
        },
        [selectedTags, handleSelectChange]
    );

    const dropdownRender = useCallback(
        (menu: React.ReactElement) => {
            if (isProcessing) {
                return (
                    <div style={{ padding: "8px", textAlign: "center" }}>
                        Processing tags...
                    </div>
                );
            }
            if (filteredTags.length === 0) {
                return (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No tags found"
                    />
                );
            }
            return (
                <List
                    height={LIST_HEIGHT}
                    itemCount={filteredTags.length}
                    itemSize={ITEM_HEIGHT}
                    width="100%"
                    itemData={{
                        tags: filteredTags,
                        recordType,
                        onOptionClick: handleOptionClick,
                        selectedTags,
                    }}
                >
                    {Row}
                </List>
            );
        },
        [
            isProcessing,
            filteredTags,
            recordType,
            handleOptionClick,
            selectedTags,
        ]
    );

    const optionsForSelectedTags = useMemo(() => {
        return selectedTags.map((tag) => {
            const tagName = tag.split("##")[1];
            const { tagType, tagFillColor, tagBorderColor } = getTagType(
                tag,
                recordType
            );
            return {
                value: tag,
                label: (
                    <CustomTag
                        key={tag}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: "4px",
                            border: `1px solid ${tagBorderColor}`,
                            overflow: "hidden",
                            width: "160px",
                            paddingRight: 5,
                            paddingLeft: 0,
                            margin: "2px",
                        }}
                    >
                        <span
                            style={{
                                width: 25,
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: tagFillColor,
                                color: "white",
                                padding: "0 8px",
                                display: "flex",
                                borderRight: `1px solid ${tagBorderColor}`,
                                flexShrink: 0,
                            }}
                        >
                            {tagType[0]}
                        </span>
                        <span
                            style={{
                                padding: "0 8px",
                                color: "black",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flexGrow: 1,
                            }}
                        >
                            {tagName}
                        </span>
                    </CustomTag>
                ),
            };
        });
    }, [selectedTags, recordType]);

    const tagRender = useCallback((props: any) => {
        const { label, closable, onClose } = props;
        return React.cloneElement(label, {
            closable,
            onClose,
            closeIcon: <FaTimes style={{ color: "white", fontSize: "12px" }} />,
            inDropdown: false,
        });
    }, []);

    const maxTagPlaceholder = useCallback(
        (omittedValues: any[]) => {
            const total = omittedValues.length + selectedTags.length - 4;
            return (
                <Popover content={omittedValues.map((item) => item.label)}>
                    <span>+{total} more</span>
                </Popover>
            );
        },
        [selectedTags.length]
    );

    return (
        <Select
            className="mb-2"
            mode="multiple"
            size="middle"
            allowClear
            tagRender={tagRender}
            style={{ width: "100%" }}
            placeholder={isProcessing ? "Processing tags..." : "Select tags"}
            maxTagCount={4}
            maxTagPlaceholder={maxTagPlaceholder}
            onChange={handleSelectChange}
            value={selectedTags}
            loading={isProcessing}
            showSearch={true}
            onSearch={setSearchText}
            popupRender={dropdownRender}
            options={optionsForSelectedTags}
            filterOption={false}
        >
        </Select>
    );
};

const MemoizedBulkModalTagDropDown = React.memo(BulkModalTagDropDown);
export default MemoizedBulkModalTagDropDown;
